/**
 * UserPermissionsPage
 *
 * Manage which users can access this WIP module -- allow or restrict per
 * user. User identity data is real (SSS `GET /users`); falls back to
 * sample data if that fetch errors or returns nothing.
 *
 * Access state (who's allowed/restricted) is now backed by the
 * standalone WIP Permissions Service (see usePermissionsService.js) --
 * a genuinely shared, cross-device store, not per-browser localStorage.
 * ProtectedRoute.jsx enforces it on every page load. If the permissions
 * service itself is unreachable, this page shows that plainly and
 * disables toggling -- it deliberately does NOT fall back to some other
 * storage, since that would quietly reintroduce the exact
 * doesn't-sync-across-devices problem this service exists to fix.
 *
 * Only admins (current user's role === 'admin') can toggle access here
 * -- enforced both here (disabled buttons, explanatory banner) AND
 * server-side by the permissions service itself (requires an
 * `x-user-role: admin` header on writes, 403s otherwise), so it's not
 * just a UI restriction a non-admin could route around via devtools.
 */
import { useMemo, useState } from 'react';
import { AlertTriangle, Info, Search, ShieldCheck, ShieldOff } from 'lucide-react';
import { useAuth } from '../hooks/useAuth.js';
import { useBranchContext } from '../hooks/useBranchContext.js';
import { useUsers } from '../hooks/useReferenceData.js';
import { usePermissionsMap, useSetUserAccessMutation } from '../hooks/usePermissionsService.js';
import { sampleUserPermissions } from '../data/sampleUserPermissionsData.js';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

export const UserPermissionsPage = () => {
  const { user: currentUser } = useAuth();
  const { activeBranchId, branches } = useBranchContext();
  const usersQuery = useUsers(activeBranchId ? { branch_id: activeBranchId } : {});
  const permissionsQuery = usePermissionsMap();
  const setAccessMutation = useSetUserAccessMutation();

  const branchNameById = useMemo(() => new Map(branches.map((b) => [b.id, b.name])), [branches]);

  const realUsers = useMemo(() => {
    if (usersQuery.isLoading || usersQuery.isError) return [];
    return (usersQuery.data ?? []).map((u) => ({
      id: String(u.id),
      name: u.name ?? u.display_name ?? `User #${u.id}`,
      email: u.email ?? '\u2014',
      role: u.role ?? 'staff',
      division: u.division ?? null,
      branchName: branchNameById.get(String(u.branch_id)) ?? '\u2014',
    }));
  }, [usersQuery.isLoading, usersQuery.isError, usersQuery.data, branchNameById]);

  const usingSampleData = !usersQuery.isLoading && (usersQuery.isError || realUsers.length === 0);
  const allUsers = usingSampleData ? sampleUserPermissions : realUsers;

  const permissionsUnreachable = permissionsQuery.isError;
  const permissionsUnauthorized = permissionsQuery.error?.response?.status === 401;
  const users = useMemo(
    () => allUsers.map((u) => ({ ...u, access: permissionsQuery.data?.[u.id]?.access ?? 'allowed' })),
    [allUsers, permissionsQuery.data]
  );

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE_OPTIONS[0]);
  const resetToFirstPage = () => setPage(1);

  const divisionOptions = useMemo(
    () => [...new Set(allUsers.map((u) => u.division).filter(Boolean))].sort(),
    [allUsers]
  );

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (statusFilter && u.access !== statusFilter) return false;
      if (divisionFilter && u.division !== divisionFilter) return false;
      if (!q) return true;
      return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    });
  }, [users, search, statusFilter, divisionFilter]);

  const allowedCount = users.filter((u) => u.access === 'allowed').length;
  const restrictedCount = users.filter((u) => u.access === 'restricted').length;
  const isAdmin = currentUser?.role === 'admin';

  const toggleAccess = (id, current) => {
    if (!isAdmin) return; // only admins can update permissions
    if (currentUser && String(currentUser.id) === id) return; // can't restrict your own account
    setAccessMutation.mutate({
      userId: id,
      access: current === 'allowed' ? 'restricted' : 'allowed',
      actingUserId: currentUser?.id,
      actingUserRole: currentUser?.role,
    });
  };

  const total = filteredUsers.length;
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  const clampedPage = Math.min(page, lastPage);
  const pageUsers = useMemo(
    () => filteredUsers.slice((clampedPage - 1) * perPage, clampedPage * perPage),
    [filteredUsers, clampedPage, perPage]
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-primary">User Permissions</h1>
          <p className="text-sm text-muted">Control who can access this WIP module.</p>
        </div>
        {usingSampleData ? (
          <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-500">
            Sample users{usersQuery.isError ? ' \u2014 live fetch failed' : ' \u2014 no live users for this filter'}
          </span>
        ) : (
          <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-500">
            Live users from SSS &middot; access via Permissions Service
          </span>
        )}
      </div>

      {!isAdmin && (
        <div className="flex items-start gap-2 rounded-md border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-sm text-sky-400">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            You can view this list, but only admins can change access. Toggling is disabled
            below.
          </span>
        </div>
      )}

      {permissionsUnreachable && (
        <div className="flex items-start gap-2 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-400">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {permissionsUnauthorized ? (
              <>
                The Permissions Service rejected this request (401) &mdash; check that{' '}
                <code className="font-mono">VITE_PERMISSIONS_API_KEY</code> here matches{' '}
                <code className="font-mono">API_KEY</code> on the service.
              </>
            ) : (
              <>
                Can&apos;t reach the Permissions Service (expects{' '}
                <code className="font-mono">VITE_PERMISSIONS_API_BASE_URL</code>).
              </>
            )}{' '}
            Access shown below defaults to &quot;Allowed&quot; and toggling is disabled until
            it&apos;s reachable again &mdash; access enforcement on other pages fails open the
            same way, so no one is incorrectly locked out while this is unresolved.
          </span>
        </div>
      )}

      <div className="flex flex-wrap gap-4">
        <div className="flex flex-1 min-w-[180px] items-center gap-3 rounded-lg border border-edge bg-surface p-4">
          <ShieldCheck className="h-5 w-5 text-emerald-500" />
          <div>
            <p className="text-xs text-muted">Allowed</p>
            <p className="text-xl font-semibold text-primary">{allowedCount}</p>
          </div>
        </div>
        <div className="flex flex-1 min-w-[180px] items-center gap-3 rounded-lg border border-edge bg-surface p-4">
          <ShieldOff className="h-5 w-5 text-rose-500" />
          <div>
            <p className="text-xs text-muted">Restricted</p>
            <p className="text-xl font-semibold text-primary">{restrictedCount}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-edge bg-surface p-3">
        <div className="flex items-center gap-2 rounded-md border border-edge bg-base px-3 py-1.5 sm:w-64">
          <Search className="h-4 w-4 text-muted" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetToFirstPage();
            }}
            placeholder="Search by name or email..."
            className="w-full bg-transparent text-sm text-primary placeholder:text-muted focus:outline-none"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            resetToFirstPage();
          }}
          className="rounded-md border border-edge bg-base px-3 py-1.5 text-sm text-primary focus:border-accent focus:outline-none"
        >
          <option value="">All Statuses</option>
          <option value="allowed">Allowed</option>
          <option value="restricted">Restricted</option>
        </select>

        <select
          value={divisionFilter}
          onChange={(e) => {
            setDivisionFilter(e.target.value);
            resetToFirstPage();
          }}
          className="rounded-md border border-edge bg-base px-3 py-1.5 text-sm text-primary focus:border-accent focus:outline-none"
        >
          <option value="">All Divisions</option>
          {divisionOptions.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>

        {(statusFilter || divisionFilter || search) && (
          <button
            type="button"
            onClick={() => {
              setStatusFilter('');
              setDivisionFilter('');
              setSearch('');
              resetToFirstPage();
            }}
            className="text-sm text-muted hover:text-primary"
          >
            Clear filters
          </button>
        )}

        <span className="ml-auto text-sm text-muted">{total} user{total === 1 ? '' : 's'}</span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-edge bg-surface">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-edge text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Division</th>
              <th className="px-4 py-3 font-medium">Branch</th>
              <th className="px-4 py-3 font-medium">Access</th>
            </tr>
          </thead>
          <tbody>
            {pageUsers.map((u) => {
              const isSelf = currentUser && String(currentUser.id) === u.id;
              const disabled = !isAdmin || isSelf || permissionsUnreachable || setAccessMutation.isPending;
              return (
                <tr key={u.id} className="border-b border-edge/60 text-primary hover:bg-surface-hover">
                  <td className="px-4 py-3">
                    {u.name}
                    {isSelf && <span className="ml-1.5 text-xs text-muted">(you)</span>}
                  </td>
                  <td className="px-4 py-3 text-muted">{u.email}</td>
                  <td className="px-4 py-3 capitalize text-muted">{u.role}</td>
                  <td className="px-4 py-3 text-muted">{u.division ?? '\u2014'}</td>
                  <td className="px-4 py-3 text-muted">{u.branchName}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => toggleAccess(u.id, u.access)}
                      disabled={disabled}
                      title={
                        !isAdmin
                          ? 'Only admins can update user permissions'
                          : isSelf
                            ? "You can't restrict your own access"
                            : permissionsUnreachable
                              ? 'Permissions Service unreachable'
                              : undefined
                      }
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                        u.access === 'allowed'
                          ? 'border-emerald-500/30 bg-emerald-500/15 text-emerald-400 hover:enabled:bg-emerald-500/25'
                          : 'border-rose-500/30 bg-rose-500/15 text-rose-400 hover:enabled:bg-rose-500/25'
                      }`}
                    >
                      {u.access === 'allowed' ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldOff className="h-3.5 w-3.5" />}
                      {u.access === 'allowed' ? 'Allowed' : 'Restricted'}
                    </button>
                  </td>
                </tr>
              );
            })}
            {pageUsers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted">
                  No users match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                resetToFirstPage();
              }}
              className="rounded-md border border-edge bg-base px-2 py-1 text-sm text-primary focus:border-accent focus:outline-none"
            >
              {PER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>
              Showing page {clampedPage} of {lastPage} ({total} rows)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={clampedPage <= 1}
                onClick={() => setPage(Math.max(1, clampedPage - 1))}
                className="rounded border border-edge px-3 py-1 disabled:opacity-40"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={clampedPage >= lastPage}
                onClick={() => setPage(Math.min(lastPage, clampedPage + 1))}
                className="rounded border border-edge px-3 py-1 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
